"use client";

import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { useModalSurface } from "@/lib/hooks/useModalSurface";
import { cn } from "./cn";

/**
 * What a dialog owes a keyboard — focus in, Tab trapped, Escape, focus
 * restored (OSO-402). That contract now lives in `useModalSurface`
 * (`lib/hooks/useModalSurface.ts`), because two surfaces that are modal but
 * are not centred boxes need it too: the mobile navigation drawer and the
 * emoji bottom sheet (OSO-638). This file is what remains — the centred panel,
 * its backdrop, and the title registry below.
 */

/**
 * Only the top-most surface answers the keyboard. Dialogs stack: the post
 * composer opens a "discard this?" confirmation on top of itself, and without an
 * order one Escape closes both while the trap underneath fights the one on top
 * for the caret.
 *
 * The stack that decides it is no longer private to this file. It used to be an
 * `openDialogs` array here, right for two dialogs and blind to the lightbox and
 * the chat dock, which own document-level Escape listeners of their own.
 * `lib/hooks/useEscapeLayer.ts` is the same idea with those in it — including
 * the rule this file established, that a dialog whose panel contains another
 * layer's panel is underneath it, because React flushes passive effects
 * child-first and mount order alone would name the outer one.
 *
 * The focus trap did not widen with it. Escape asks "is anything drawn over
 * me"; Tab asks "is another *modal* thing over me", and the answers differ
 * because most of what can be drawn over a dialog is not modal and traps
 * nothing. The hover card is the sharp edge: `z-[200]`, so resting the pointer
 * on an author name inside an open dialog makes it topmost, and a trap that
 * stood down for it would let the next Tab walk out into the page behind. So Tab
 * asks the narrower `"rank"` question, the `topmostDialogId()` this file owned.
 * Both questions are asked from `useModalSurface` now.
 */

/**
 * How `DialogTitle` tells the panel what to name itself with.
 *
 * The id is registered rather than assumed: a panel pointing `aria-labelledby`
 * at an id nothing rendered names itself with a dangling reference, worse for a
 * screen reader than no name at all — which is what a seventh caller with
 * neither a `DialogTitle` nor a `label` used to get. It also lets a
 * `DialogTitle` carrying its own `id` be the one the panel points at.
 */
interface DialogTitleRegistry {
  id: string;
  register: (id: string | undefined) => void;
}
const DialogTitleIdContext = createContext<DialogTitleRegistry | undefined>(
  undefined,
);

export interface DialogProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  /** "center" (default) vertically centers; "top" pins near the top with
   *  some padding, for a dialog opened from deep in a scrolled page. */
  align?: "center" | "top";
  /** The dialog's accessible name when it renders no `DialogTitle` — a
   *  dialog that announces itself as nothing but "dialog" tells a screen
   *  reader user only that they are stuck. */
  label?: string;
}

/**
 * Modal shell with a dimmed backdrop. Controlled via `open`; clicking the
 * backdrop or pressing Escape calls `onClose`. Compose with the Dialog* parts.
 */
export function Dialog({
  open,
  onClose,
  children,
  className,
  align = "center",
  label,
}: DialogProps) {
  const panelRef = useModalSurface<HTMLDivElement>({ open, onClose });
  const titleId = useId();

  // Set by whichever `DialogTitle` actually rendered — `undefined` while none
  // has, which is what keeps `aria-labelledby` off a dialog that has no title
  // to point at. See `DialogTitleIdContext`.
  const [titledBy, setTitledBy] = useState<string | undefined>(undefined);
  const register = useCallback((id: string | undefined) => setTitledBy(id), []);
  const titleRegistry = useMemo(
    () => ({ id: titleId, register }),
    [titleId, register],
  );

  if (!open) return null;
  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex justify-center p-4",
        align === "top" ? "items-start pt-24" : "items-center",
      )}
    >
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-labelledby={label ? undefined : titledBy}
        // The panel itself is the focus of last resort, for a dialog whose
        // content is pure prose. Not a Tab stop — `focusablesIn` skips -1.
        tabIndex={-1}
        className={cn(
          // BOUNDED, AND THE BODY IS WHAT SCROLLS. The panel had neither a
          // height limit nor an overflow rule, so a dialog taller than the
          // viewport simply overflowed a `fixed inset-0` container and was
          // clipped with nothing to scroll — the bot transcript hit this on
          // any phone, where the reader could see the first few turns and had
          // no way to reach the rest.
          //
          // `100dvh` rather than `100vh` because mobile browsers shrink the
          // visible area as their own chrome appears, and `vh` keeps measuring
          // the larger one — the exact bottom slice that was unreachable.
          // `2rem` is the wrapper's `p-4` on both edges. `flex flex-col` with
          // `min-h-0` on the body (see DialogBody) is what makes the header
          // and footer hold still while the middle scrolls, rather than the
          // whole panel scrolling its own title out of view.
          "relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col bg-surface border border-border rounded-card shadow-xl animate-fade-in outline-none",
          className,
        )}
      >
        <DialogTitleIdContext.Provider value={titleRegistry}>
          {children}
        </DialogTitleIdContext.Provider>
      </div>
    </div>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("px-5 py-4 border-b border-border space-y-1", className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, id, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  const registry = useContext(DialogTitleIdContext);
  const titleId = id ?? registry?.id;
  // A layout effect, so the panel is named before the browser paints it —
  // and unregistered on unmount, because a title rendered conditionally must
  // not leave the panel pointing at an id that is no longer in the document.
  useLayoutEffect(() => {
    if (!registry) return;
    registry.register(titleId);
    return () => registry.register(undefined);
  }, [registry, titleId]);
  return (
    <h2
      id={titleId}
      className={cn("font-semibold text-foreground leading-tight", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-sm text-muted-foreground", className)} {...props} />
  );
}

export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // `min-h-0` is load-bearing: a flex child's default `min-height: auto`
  // refuses to shrink below its content, so without it the body pushes the
  // panel past `max-h` and `overflow-y-auto` never has anything to do.
  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", className)} {...props} />
  );
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "px-5 py-4 border-t border-border flex items-center justify-end gap-3",
        className,
      )}
      {...props}
    />
  );
}
